import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Users, UserCheck, CreditCard, TrendingUp, AlertCircle, Award, BookOpen, GraduationCap, Swords, Hand, Users2, Star, FileText, FileSpreadsheet, ArrowUpCircle, ScrollText, Settings, Coins } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const DEFAULT_TARGET = 500;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Proficiency level colors
const PROFICIENCY_COLORS = {
  "Novice": "bg-gray-500",
  "Foundation": "bg-blue-500",
  "Complete Form": "bg-cyan-500",
  "Good": "bg-green-500",
  "Good Proficiency": "bg-green-500",
  "Refining": "bg-yellow-500",
  "Functional": "bg-orange-500",
  "Instructor": "bg-purple-500",
  "Instructor Level": "bg-purple-500",
  "Introduced": "bg-gray-500",
};

// Adult pillar colors with gradient support
const PILLAR_COLORS = {
  "Chou Kup 初級 – Beginner": "bg-gray-500",
  "Jung Kup 中級 – Intermediate": "bg-blue-500",
  "Gou Kup 高級 – Advanced": "bg-orange-500",
  "Gau Lin – Instructor": "bg-purple-500",
};

// Pillar level ranges for progress calculation
const PILLAR_LEVEL_RANGES = {
  "Chou Kup 初級 – Beginner": { min: 1, max: 3 },
  "Jung Kup 中級 – Intermediate": { min: 4, max: 6 },
  "Gou Kup 高級 – Advanced": { min: 7, max: 9 },
  "Gau Lin – Instructor": { min: 10, max: 12 },
};

// Helper: Calculate % progress within a pillar based on level
const calculatePillarProgress = (pillar, levelStr) => {
  if (!pillar || !levelStr) return 0;
  const range = PILLAR_LEVEL_RANGES[pillar];
  if (!range) return 0;
  
  try {
    const levelNum = parseInt(levelStr.split(".")[0]);
    if (levelNum < range.min) return 0;
    if (levelNum > range.max) return 100;
    const progress = ((levelNum - range.min + 1) / (range.max - range.min + 1)) * 100;
    return Math.round(progress);
  } catch {
    return 0;
  }
};

// Helper: Get level number from level string
const getLevelNum = (levelStr) => {
  if (!levelStr) return 0;
  try {
    return parseInt(levelStr.split(".")[0]);
  } catch {
    return 0;
  }
};

// Belt colors for kids
const BELT_COLORS = {
  "White": "bg-gray-100 text-black",
  "Yellow": "bg-yellow-400 text-black",
  "Orange": "bg-orange-400 text-black",
  "Green": "bg-green-400 text-black",
  "Blue": "bg-blue-400 text-black",
  "Purple": "bg-purple-400 text-black",
  "Brown": "bg-amber-700 text-white",
  "Red": "bg-red-600 text-white",
  "Black": "bg-gray-900 text-white",
};

function RevenueGauge({ value, target }) {
  const pct = Math.min(value / target, 1.5);
  const displayPct = Math.min(pct * 100, 150);
  const color = pct >= 1 ? "#22c55e" : pct >= 0.6 ? "#eab308" : "#ef4444";
  const label = pct >= 1 ? "TARGET HIT" : pct >= 0.6 ? "ON TRACK" : "BEHIND";
  // v7.3: 2x larger gauge on desktop, responsive on mobile
  const cx = 160, cy = 150, r = 120, stroke = 20;
  const circumference = Math.PI * r;
  const filledLen = Math.min(pct, 1) * circumference;
  const emptyLen = circumference - filledLen;
  const needleAngle = 180 - Math.min(pct, 1) * 180;
  const needleRad = (needleAngle * Math.PI) / 180;
  const needleLen = r - 16;
  const nx = cx + needleLen * Math.cos(needleRad);
  const ny = cy - needleLen * Math.sin(needleRad);
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[200px] h-[110px] sm:w-[240px] sm:h-[135px] md:w-[320px] md:h-[180px]">
        <svg width="100%" height="100%" viewBox="0 0 320 180" preserveAspectRatio="xMidYMid meet">
          <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#27272a" strokeWidth={stroke} strokeLinecap="round" />
          <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${filledLen} ${emptyLen}`} />
          <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#fff" strokeWidth="3" strokeLinecap="round" />
          <circle cx={cx} cy={cy} r="6" fill="#fff" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-1 md:pb-2">
          <p className="text-xl md:text-3xl font-bold text-white leading-none">&pound;{value.toFixed(0)}</p>
          <p className="text-[9px] md:text-xs text-[#A1A1AA]">of &pound;{target}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-1">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-xs md:text-sm font-semibold tracking-wide" style={{ color }}>{label}</span>
        <span className="text-[10px] md:text-xs text-[#A1A1AA]">({displayPct.toFixed(0)}%)</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [monthlyTarget, setMonthlyTarget] = useState(DEFAULT_TARGET);
  const [reminderThreshold, setReminderThreshold] = useState(7);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, attendanceRes, feePaymentsRes, paymentsRes, privateSessionsRes, targetRes, thresholdRes] = await Promise.allSettled([
        axios.get(`${API}/students`),
        axios.get(`${API}/attendance`),
        axios.get(`${API}/fee-payments`),
        axios.get(`${API}/payments`),
        axios.get(`${API}/private-sessions`),
        axios.get(`${API}/settings/monthly_target`),
        axios.get(`${API}/settings/reminder_threshold_days`),
      ]);
      if (studentsRes.status === 'fulfilled') setStudents(studentsRes.value?.data || []);
      if (attendanceRes.status === 'fulfilled') setAttendance(attendanceRes.value?.data || []);
      
      const paymentsData = paymentsRes.status === 'fulfilled' ? (paymentsRes.value?.data || []) : [];
      setAllPayments(paymentsData);
      
      // Load monthly target from settings API
      if (targetRes.status === 'fulfilled' && targetRes.value?.data?.value) {
        setMonthlyTarget(Number(targetRes.value.data.value) || DEFAULT_TARGET);
      } else {
        try {
          const saved = localStorage.getItem('loongfu_monthly_target');
          if (saved && Number(saved) > 0) setMonthlyTarget(Number(saved));
        } catch {}
      }

      // Load reminder threshold
      if (thresholdRes.status === 'fulfilled' && thresholdRes.value?.data?.value) {
        setReminderThreshold(Number(thresholdRes.value.data.value) || 7);
      }

      // Calculate this month's PAID revenue only
      const now = new Date();
      const cm = now.getMonth();
      const cy = now.getFullYear();
      let total = 0;

      // Group payments (monthly subscriptions) - ONLY PAID
      paymentsData.forEach(p => {
        if (p.status === "paid" && p.month === cm + 1 && p.year === cy) total += parseFloat(p.amount) || 0;
      });
      // Fee payments (grading, membership) - one-off paid items
      if (feePaymentsRes.status === 'fulfilled') {
        (feePaymentsRes.value?.data || []).forEach(p => {
          if (p.payment_status === "unpaid") return;
          const d = new Date(p.date);
          if (!isNaN(d) && d.getMonth() === cm && d.getFullYear() === cy) total += parseFloat(p.amount) || 0;
        });
      }
      // Private sessions - only paid (use actual saved price)
      if (privateSessionsRes.status === 'fulfilled') {
        (privateSessionsRes.value?.data || []).forEach(s => {
          if (s.payment_status !== "Paid" || s.status === "No-Charge") return;
          const d = new Date(s.session_date);
          if (!isNaN(d) && d.getMonth() === cm && d.getFullYear() === cy) {
            total += s.price != null ? parseFloat(s.price) : 60;
          }
        });
      }
      setMonthlyRevenue(total);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Classify students: adult classes vs kid classes
  const isAdultStudent = (s) => {
    const ct = (s.class_type || '').toLowerCase();
    const cts = (s.class_types || []).map(c => c.toLowerCase());
    const all = [ct, ...cts].join(' ');
    if (all.includes('adult')) return true;
    if (all.includes('tiger') || all.includes('dragon')) return false;
    // Fallback: if has adult_pillar or adult_level, treat as adult
    if (s.adult_pillar || s.adult_level) return true;
    // Age fallback from DOB
    if (s.date_of_birth) {
      try {
        const dob = new Date(s.date_of_birth);
        if (!isNaN(dob)) {
          const age = (Date.now() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
          return age >= 16;
        }
      } catch {}
    }
    return false;
  };

  // v12.0.5: Session-based logic — Dashboard shows ALL students in totals.
  // Attendance records only come from the Attendance Register (group classes),
  // so Private/Remote sessions are already excluded from attendance counts.
  const adultStudents = students.filter(isAdultStudent);
  const kidsStudents = students.filter(s => !isAdultStudent(s));

  // Weekly check-ins (Monday start)
  const weeklyCheckins = (() => {
    const now = new Date();
    const day = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
    monday.setHours(0, 0, 0, 0);
    const seen = new Set();
    return attendance.filter(a => {
      let d;
      if (a.date && a.date.includes('/')) {
        const p = a.date.split('/');
        d = new Date(`${p[2]}-${p[1]}-${p[0]}`);
      } else {
        d = new Date(a.date || a.checked_in_at);
      }
      if (isNaN(d) || d < monday) return false;
      const key = `${a.student_id}_${d.toISOString().split('T')[0]}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).length;
  })();

  const monthlyAttendance = (() => {
    const now = new Date();
    return attendance.filter(a => {
      let d;
      if (a.date && a.date.includes('/')) {
        const p = a.date.split('/');
        d = new Date(`${p[2]}-${p[1]}-${p[0]}`);
      } else {
        d = new Date(a.date || a.checked_in_at);
      }
      return !isNaN(d) && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  })();

  // Class breakdown (normalize old names)
  const classBreakdown = {};
  const classNameMap = { 'Private Only': 'Private Lessons', 'International': 'Remote/International' };
  students.forEach(s => {
    const classes = s.class_types?.length ? s.class_types : (s.class_type ? [s.class_type] : []);
    classes.forEach(c => { 
      const normalized = classNameMap[c] || c;
      classBreakdown[normalized] = (classBreakdown[normalized] || 0) + 1; 
    });
  });

  // Belt distribution (from all students who have belt_rank)
  const beltDistribution = {};
  students.forEach(s => {
    if (s.belt_rank) beltDistribution[s.belt_rank] = (beltDistribution[s.belt_rank] || 0) + 1;
  });

  // Adult pillar distribution
  const pillarDistribution = {};
  adultStudents.forEach(s => {
    const p = s.adult_pillar || "Not Set";
    pillarDistribution[p] = (pillarDistribution[p] || 0) + 1;
  });

  // Adult level distribution
  const levelDistribution = {};
  adultStudents.forEach(s => {
    if (s.adult_level) {
      const num = parseInt(String(s.adult_level).split('.')[0]);
      if (num >= 1 && num <= 12) levelDistribution[num] = (levelDistribution[num] || 0) + 1;
    }
  });

  // Sifu count
  const sifuCount = students.filter(s => s.is_sifu === 1 || s.is_sifu === true || s.is_sifu === '1').length;

  // Safe defaults for sections that need specialized API endpoints
  const promotionEligible = [];
  const attendanceTrends = null;

  // Unpaid payments for current month (exclude exempt students)
  const unpaidThisMonth = allPayments.filter(p => {
    if (p.status !== "unpaid" || p.month !== new Date().getMonth() + 1 || p.year !== new Date().getFullYear()) return false;
    const student = students.find(s => s.id === p.student_id);
    return student && student.billing_type !== "exempt";
  });
  const unpaidCount = unpaidThisMonth.length;
  const unpaidAmount = unpaidThisMonth.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  // Late Payers: unpaid payments older than threshold days
  // EXCLUDES exempt students. pay_as_you_go only if they have a specific unpaid record.
  const getLatePayers = () => {
    const now = new Date();
    const threshold = reminderThreshold || 7;
    const late = allPayments.filter(p => {
      if (p.status !== "unpaid") return false;
      const student = students.find(s => s.id === p.student_id);
      if (!student) return false;
      // Exclude exempt students entirely
      if (student.billing_type === "exempt") return false;
      // Calculate due date: 1st of the payment month
      const dueDate = new Date(p.year, p.month - 1, 1);
      const diffDays = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
      return diffDays >= threshold;
    });
    return late;
  };
  const latePayers = getLatePayers();

  // Group late payers by class
  const lateByClass = {};
  latePayers.forEach(p => {
    const student = students.find(s => s.id === p.student_id);
    if (!student) return;
    const classes = student.class_types?.length ? student.class_types : (student.class_type ? [student.class_type] : ["Unknown"]);
    classes.forEach(cls => {
      if (!lateByClass[cls]) lateByClass[cls] = [];
      lateByClass[cls].push({ ...p, studentName: student.name, studentId: student.id });
    });
  });
  const totalLateAmount = latePayers.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const gradesSummary = null;
  const membershipStatus = null;

  // Generate quick CSV report from local data
  const downloadQuickCSV = () => {
    if (!students.length) { toast.error("No student data available"); return; }
    const headers = ["Name", "Class", "Rank", "Type"];
    const rows = students.map(s => [
      s.name || 'Unknown', (s.class_types || [s.class_type]).join('; '), s.belt_rank || 'N/A', isAdultStudent(s) ? 'Adult' : 'Kid'
    ]);
    const csvContent = [
      `Student Report - ${new Date().toLocaleDateString()}`,
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `student_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  // Export grades summary from local data
  const downloadGradesCSV = () => {
    let csvContent = "Grades Summary Report\n\nBelt Distribution\nBelt,Count\n";
    Object.entries(beltDistribution).forEach(([belt, count]) => {
      csvContent += `"${belt}",${count}\n`;
    });
    csvContent += "\nAdult Pillar Distribution\nPillar,Count\n";
    Object.entries(pillarDistribution).forEach(([pillar, count]) => {
      csvContent += `"${pillar}",${count}\n`;
    });
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `grades_summary_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Grades report downloaded");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#A1A1AA]">Loading...</div>
      </div>
    );
  }

  const currentMonth = MONTH_NAMES[new Date().getMonth()];
  const currentYear = new Date().getFullYear();

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Dashboard 儀表板
        </h1>
        <p className="text-[#A1A1AA] mt-1">{currentMonth} {currentYear} Overview</p>
      </div>

      {/* Top KPIs - Always visible */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Students */}
        <Card className="bg-[#18181b] border-white/10 rounded-sm hover:border-white/20 transition-all">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">Total Students</p>
                <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {students.length}
                </p>
                <div className="flex gap-4 mt-2 text-xs text-[#A1A1AA]">
                  <span>Kids: {kidsStudents.length}</span>
                  <span>Adults: {adultStudents.length}</span>
                </div>
              </div>
              <div className="p-3 bg-[#27272a] rounded-sm">
                <Users className="w-6 h-6 text-[#D4AF37]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Check-ins */}
        <Card className="bg-[#18181b] border-white/10 rounded-sm hover:border-white/20 transition-all">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">Weekly Check-ins</p>
                <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {weeklyCheckins}
                </p>
                <p className="text-xs text-[#A1A1AA] mt-2">Monthly: {monthlyAttendance}</p>
              </div>
              <div className="p-3 bg-[#27272a] rounded-sm">
                <UserCheck className="w-6 h-6 text-[#22c55e]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Income - PAID ONLY */}
        <Card className="bg-[#18181b] border-white/10 rounded-sm hover:border-white/20 transition-all">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">Monthly Income</p>
                <p data-testid="monthly-income-value" className="text-4xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  &pound;{monthlyRevenue.toFixed(2)}
                </p>
                <p className="text-xs text-[#A1A1AA] mt-2">Paid this month</p>
              </div>
              <div className="p-3 bg-[#27272a] rounded-sm">
                <TrendingUp className="w-6 h-6 text-[#D4AF37]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unpaid */}
        <Card className="bg-[#18181b] border-white/10 rounded-sm hover:border-white/20 transition-all">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">Unpaid This Month</p>
                <p data-testid="unpaid-count" className="text-4xl font-bold text-[#ef4444]" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {unpaidCount}
                </p>
                <p className="text-xs text-[#A1A1AA] mt-2">&pound;{unpaidAmount.toFixed(2)} outstanding</p>
              </div>
              <div className="p-3 bg-[#27272a] rounded-sm">
                <AlertCircle className="w-6 h-6 text-[#ef4444]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Adults vs Kids */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#18181b] border border-white/10 p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]">
            Overview
          </TabsTrigger>
          <TabsTrigger value="adults" className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]">
            <Award className="w-4 h-4 mr-2" />
            Adults ({adultStudents.length})
          </TabsTrigger>
          <TabsTrigger value="kids" className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]">
            <GraduationCap className="w-4 h-4 mr-2" />
            Kids ({kidsStudents.length})
          </TabsTrigger>
        </TabsList>

        {/* ==========================================
            OVERVIEW TAB - Class breakdown & summary
            ========================================== */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Students by Class */}
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-6">
                <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Students by Class
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {Object.keys(classBreakdown).length > 0 ? (
                    Object.entries(classBreakdown).map(([classType, count]) => (
                      <div key={classType} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            classType.includes("Tigers") ? "bg-orange-400" :
                            classType.includes("Dragons") ? "bg-blue-400" :
                            classType.includes("Tai Chi") ? "bg-teal-400" :
                            classType.includes("Private") ? "bg-[#D4AF37]" :
                            "bg-red-500"
                          }`} />
                          <span className="text-[#EDEDED]">{classType}</span>
                        </div>
                        <span className="text-xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-[#A1A1AA] text-sm">No class data available</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Revenue Health Gauge */}
            <Card data-testid="revenue-health-widget" className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-6">
                <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Revenue Health
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 flex flex-col items-center">
                <RevenueGauge value={monthlyRevenue} target={monthlyTarget} />
                <div data-testid="dashboard-target-setting" className="flex items-center gap-2 mt-3">
                  <Settings className="w-3 h-3 text-[#A1A1AA]" />
                  <span className="text-[#A1A1AA] text-[10px]">Target &pound;</span>
                  <input
                    data-testid="dashboard-monthly-target-input"
                    type="number"
                    min="100"
                    step="50"
                    value={monthlyTarget}
                    onChange={e => {
                      const num = parseFloat(e.target.value);
                      if (e.target.value === '') { setMonthlyTarget(''); return; }
                      if (isFinite(num) && num > 0) {
                        setMonthlyTarget(num);
                        try { localStorage.setItem('loongfu_monthly_target', String(num)); } catch {}
                        axios.put(`${API}/settings/monthly_target`, { value: num }).catch(() => {});
                      }
                    }}
                    className="w-16 bg-white/5 border border-white/10 rounded text-white text-xs text-center px-1 py-0.5 focus:outline-none focus:border-[#D4AF37]/50"
                    onBlur={() => {
                      if (!monthlyTarget || monthlyTarget <= 0) {
                        setMonthlyTarget(DEFAULT_TARGET);
                        try { localStorage.setItem('loongfu_monthly_target', String(DEFAULT_TARGET)); } catch {}
                      }
                    }}
                  />
                </div>
                <p className="text-[#A1A1AA] text-xs mt-2">{currentMonth} {currentYear}</p>
              </CardContent>
            </Card>
          </div>

          {/* Late Payers Widget */}
          {latePayers.length > 0 && (
            <Card data-testid="late-payers-widget" className="bg-[#18181b] border-[#ef4444]/30 rounded-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-[#ef4444]" />
                    <h3 className="text-base font-semibold text-[#ef4444] uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      Late Payers ({latePayers.length})
                    </h3>
                    <span className="text-xs text-[#A1A1AA] ml-2">&pound;{totalLateAmount.toFixed(2)} overdue</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#A1A1AA]">Threshold</span>
                    <select
                      data-testid="reminder-threshold-select"
                      value={reminderThreshold}
                      onChange={e => {
                        const val = parseInt(e.target.value);
                        setReminderThreshold(val);
                        axios.put(`${API}/settings/reminder_threshold_days`, { value: val }).catch(() => {});
                      }}
                      className="bg-white/5 border border-white/10 rounded text-white text-xs px-2 py-1 focus:outline-none"
                    >
                      <option value="7">7 days</option>
                      <option value="14">14 days</option>
                      <option value="21">21 days</option>
                      <option value="30">30 days</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {Object.entries(lateByClass).map(([cls, payers]) => (
                    <div key={cls} className="bg-[#27272a] p-3 rounded-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${
                          cls.includes("Tigers") ? "bg-orange-400" :
                          cls.includes("Dragons") ? "bg-blue-400" :
                          cls.includes("Tai Chi") ? "bg-teal-400" :
                          cls.includes("Private") ? "bg-[#D4AF37]" :
                          "bg-red-500"
                        }`} />
                        <span className="text-white text-sm font-semibold">{cls}</span>
                        <span className="text-[#A1A1AA] text-xs ml-auto">{payers.length}</span>
                      </div>
                      {payers.slice(0, 4).map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1 border-t border-white/5">
                          <span className="text-[#EDEDED] truncate max-w-[120px]">{p.studentName}</span>
                          <span className="text-[#ef4444] font-medium">&pound;{parseFloat(p.amount).toFixed(0)}</span>
                        </div>
                      ))}
                      {payers.length > 4 && <p className="text-[#A1A1AA] text-[10px] mt-1">+{payers.length - 4} more</p>}
                    </div>
                  ))}
                </div>
                <Link to="/payments" className="text-xs text-[#D4AF37] hover:underline mt-3 inline-block">
                  View all reminders &rarr;
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Promotion Notifications - Show if students are eligible */}
          {promotionEligible.length > 0 && (
            <Card className="bg-[#18181b] border-[#D4AF37]/50 rounded-sm">
              <CardHeader className="border-b border-white/5 p-4">
                <div className="flex items-center gap-2">
                  <ArrowUpCircle className="w-5 h-5 text-[#D4AF37]" />
                  <CardTitle className="text-base font-semibold text-[#D4AF37] uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Promotion Ready ({promotionEligible.length})
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-[#A1A1AA] text-sm mb-3">The following students meet promotion requirements:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {promotionEligible.slice(0, 6).map((student) => (
                    <div key={student.student_id} className="flex items-center justify-between bg-[#27272a] p-3 rounded-sm">
                      <div>
                        <p className="text-white font-medium text-sm">{student.name}</p>
                        <p className="text-[#A1A1AA] text-xs">{student.current_rank}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-[#22c55e] text-sm font-bold">{student.attendance_rate}%</span>
                        <p className="text-[#A1A1AA] text-[10px]">{student.completed_forms} forms</p>
                      </div>
                    </div>
                  ))}
                </div>
                {promotionEligible.length > 6 && (
                  <p className="text-[#A1A1AA] text-sm mt-2 text-center">+ {promotionEligible.length - 6} more students</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Sifu's Master Log - Big Button */}
          <Link to="/sifu-ledger" className="block">
            <Card className="bg-gradient-to-r from-[#18181b] to-[#1f1f22] border-[#D4AF37]/50 rounded-sm hover:border-[#D4AF37] hover:shadow-[0_0_20px_rgba(212,175,55,0.2)] transition-all group cursor-pointer" data-testid="sifu-ledger-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-4 bg-[#D4AF37]/20 rounded-sm group-hover:bg-[#D4AF37]/30 transition-all">
                      <ScrollText className="w-8 h-8 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-[#D4AF37] uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                        師父 Sifu's Master Log
                      </h2>
                      <p className="text-[#A1A1AA] text-sm mt-1">Track form proficiency for all students</p>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 text-[#D4AF37] group-hover:translate-x-1 transition-transform">
                    <span className="text-sm font-medium">Open Log</span>
                    <ArrowUpCircle className="w-5 h-5 rotate-90" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Quick Actions */}
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-6">
              <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/attendance" className="flex items-center gap-3 p-4 bg-[#27272a] rounded-sm hover:bg-[#3f3f46] transition-all group" data-testid="quick-action-checkin">
                  <UserCheck className="w-6 h-6 text-[#22c55e] group-hover:scale-110 transition-transform" />
                  <span className="text-white font-medium">Check-in Student</span>
                </Link>
                <Link to="/students?action=add" className="flex items-center gap-3 p-4 bg-[#27272a] rounded-sm hover:bg-[#3f3f46] transition-all group" data-testid="quick-action-add-student">
                  <Users className="w-6 h-6 text-[#D4AF37] group-hover:scale-110 transition-transform" />
                  <span className="text-white font-medium">Add Student</span>
                </Link>
                <Link to="/payments?action=add" className="flex items-center gap-3 p-4 bg-[#27272a] rounded-sm hover:bg-[#3f3f46] transition-all group" data-testid="quick-action-payment">
                  <CreditCard className="w-6 h-6 text-[#D92323] group-hover:scale-110 transition-transform" />
                  <span className="text-white font-medium">Record Payment</span>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Report Downloads */}
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-4">
              <CardTitle className="text-base font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Attendance Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={downloadQuickCSV}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download Monthly CSV
                </Button>
                <Button
                  onClick={downloadGradesCSV}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download Grades CSV
                </Button>
                <Link to="/attendance-reports">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <FileText className="w-4 h-4 mr-2" />
                    View Full Reports
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Attendance Trends Chart */}
          {attendanceTrends && attendanceTrends.trends && Object.keys(attendanceTrends.trends).length > 0 && (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-4">
                <CardTitle className="text-base font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Weekly Attendance Trends
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-4">
                  {Object.entries(attendanceTrends.trends).map(([classType, weeks]) => {
                    if (!Array.isArray(weeks) || weeks.length === 0) return null;
                    return (
                      <div key={classType}>
                        <p className="text-[#A1A1AA] text-sm mb-2">{classType}</p>
                        <div className="flex items-end gap-2 h-16">
                          {weeks.map((w, idx) => {
                            const maxCount = Math.max(...weeks.map(x => x?.count || 0), 1);
                            const height = ((w?.count || 0) / maxCount) * 100;
                            return (
                              <div key={idx} className="flex-1 flex flex-col items-center">
                                <div 
                                  className={`w-full rounded-t-sm ${
                                    classType.includes("Tigers") ? "bg-orange-500" : 
                                    classType.includes("Dragons") ? "bg-blue-500" : "bg-red-500"
                                  }`}
                                  style={{ height: `${Math.max(height, 5)}%` }}
                                  title={`${w?.week || ''}: ${w?.count || 0} check-ins`}
                                />
                                <span className="text-[10px] text-[#A1A1AA] mt-1">{w?.week || ''}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rank/Belt Distribution */}
          {gradesSummary && gradesSummary.belt_by_class && Object.keys(gradesSummary.belt_by_class).length > 0 && (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-4">
                <CardTitle className="text-base font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Rank Distribution by Class
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(gradesSummary.belt_by_class).map(([classType, ranks]) => {
                    if (!ranks || typeof ranks !== 'object') return null;
                    return (
                      <div key={classType} className="bg-[#27272a] p-3 rounded-sm">
                        <p className="text-white font-medium mb-2">{classType.replace(" Kung Fu", "")}</p>
                        <div className="space-y-1">
                          {Object.entries(ranks).slice(0, 5).map(([rank, count]) => (
                            <div key={rank} className="flex items-center justify-between text-sm">
                              <span className="text-[#A1A1AA]">{rank}</span>
                              <span className="text-white font-bold">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Membership Status Alerts */}
          {membershipStatus && ((membershipStatus.expired && membershipStatus.expired.length > 0) || (membershipStatus.expiring_soon && membershipStatus.expiring_soon.length > 0)) && (
            <Card className="bg-[#18181b] border-red-500/30 rounded-sm">
              <CardHeader className="border-b border-white/5 p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <CardTitle className="text-base font-semibold text-red-400 uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Membership Alerts
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {membershipStatus.expired && membershipStatus.expired.length > 0 && (
                    <div>
                      <p className="text-red-400 text-sm font-medium mb-2">Expired ({membershipStatus.expired.length})</p>
                      {membershipStatus.expired.slice(0, 3).map(s => (
                        <div key={s?.id || Math.random()} className="flex items-center justify-between bg-red-500/10 p-2 rounded-sm mb-1">
                          <span className="text-white text-sm">{s?.name || 'Unknown'}</span>
                          <span className="text-red-400 text-xs">{s?.expiry_date || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {membershipStatus.expiring_soon && membershipStatus.expiring_soon.length > 0 && (
                    <div>
                      <p className="text-yellow-400 text-sm font-medium mb-2">Expiring Soon ({membershipStatus.expiring_soon.length})</p>
                      {membershipStatus.expiring_soon.slice(0, 3).map(s => (
                        <div key={s?.id || Math.random()} className="flex items-center justify-between bg-yellow-500/10 p-2 rounded-sm mb-1">
                          <span className="text-white text-sm">{s?.name || 'Unknown'}</span>
                          <span className="text-yellow-400 text-xs">{s?.days_until || 0} days</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==========================================
            ADULTS TAB - Pillar, Level, Forms Progress
            ========================================== */}
        <TabsContent value="adults" className="space-y-6">
          {/* Adult Stats Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#D4AF37]/20 rounded-sm">
                    <Award className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">師父 Sifu</p>
                    <p className="text-3xl font-bold text-[#D4AF37]" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      {sifuCount}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-500/20 rounded-sm">
                    <GraduationCap className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Gau Lin</p>
                    <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      {adultStudents.filter(s => (s.adult_pillar || '').includes('Gau Lin')).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-500/20 rounded-sm">
                    <TrendingUp className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Gou Kup</p>
                    <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      {adultStudents.filter(s => (s.adult_pillar || '').includes('Gou Kup')).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500/20 rounded-sm">
                    <Users className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Total Adults</p>
                    <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      {adultStudents.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Adult Pillar & Level Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Pillar Distribution */}
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-4">
                <CardTitle className="text-base font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Pillar Distribution
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {Object.entries(pillarDistribution)
                    .filter(([pillar]) => pillar !== "Not Set")
                    .map(([pillar, count]) => {
                      const totalAdults = adultStudents.length || 1;
                      const percentage = Math.round((count / totalAdults) * 100) || 0;
                      return (
                        <div key={pillar} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#EDEDED]">{pillar.split(" – ")[0]}</span>
                            <span className="text-white font-bold">{count} <span className="text-[#A1A1AA] font-normal">({percentage}%)</span></span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${PILLAR_COLORS[pillar] || "bg-gray-500"} transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  {(pillarDistribution["Not Set"] || 0) > 0 && (
                    <div className="pt-2 border-t border-white/10">
                      <div className="flex items-center justify-between text-sm text-[#A1A1AA]">
                        <span>Not Assigned</span>
                        <span>{pillarDistribution["Not Set"]}</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Level Distribution */}
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-4">
                <CardTitle className="text-base font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Level Distribution (1-12)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-6 gap-2">
                  {[...Array(12)].map((_, i) => {
                    const level = i + 1;
                    const count = levelDistribution[level] || 0;
                    const pillarColor = level <= 3 ? "bg-gray-500" : level <= 6 ? "bg-blue-500" : level <= 9 ? "bg-orange-500" : "bg-purple-500";
                    return (
                      <div 
                        key={level} 
                        className={`p-2 rounded-sm text-center ${count > 0 ? pillarColor : "bg-white/5"}`}
                        title={`Level ${level}: ${count} students`}
                      >
                        <p className={`text-xs ${count > 0 ? "text-white/80" : "text-[#A1A1AA]"}`}>L{level}</p>
                        <p className={`text-lg font-bold ${count > 0 ? "text-white" : "text-[#A1A1AA]"}`} style={{ fontFamily: 'Oswald, sans-serif' }}>
                          {count}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-4 mt-4 text-xs">
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-gray-500" /><span className="text-[#A1A1AA]">Chou Kup</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-blue-500" /><span className="text-[#A1A1AA]">Jung Kup</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-orange-500" /><span className="text-[#A1A1AA]">Gou Kup</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-purple-500" /><span className="text-[#A1A1AA]">Gau Lin</span></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Adult Students List with Progress */}
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-6">
              <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Adult Students Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {adultStudents.length === 0 ? (
                <p className="text-[#A1A1AA] text-center py-4">No adult students enrolled</p>
              ) : (
                <div className="space-y-4">
                  {adultStudents.map((student) => {
                    const pillarProgress = calculatePillarProgress(student.adult_pillar, student.adult_level);
                    const levelNum = getLevelNum(student.adult_level);
                    
                    // Group forms by category
                    const handForms = student.forms_in_progress?.filter(f => f.category === "Hand Form") || [];
                    const weapons = student.forms_in_progress?.filter(f => f.category === "Weapons") || [];
                    const sparring = student.forms_in_progress?.filter(f => f.category === "Sparring Set") || [];
                    
                    return (
                      <div key={student.id} className="bg-[#27272a] rounded-sm p-4" data-testid={`adult-student-${student.id}`}>
                        <div className="flex flex-col xl:flex-row xl:items-start gap-4">
                          {/* Student Identity & Rank */}
                          <div className="flex items-start gap-4 xl:w-64 flex-shrink-0">
                            <div className="flex-shrink-0">
                              {student.is_sifu ? (
                                <div className="w-14 h-14 rounded-sm bg-gradient-to-br from-[#D4AF37] to-[#b8962e] flex items-center justify-center shadow-lg">
                                  <span className="text-black font-bold text-xl">師</span>
                                </div>
                              ) : (
                                <div className="w-14 h-14 rounded-sm bg-[#D92323] flex items-center justify-center">
                                  <span className="text-white font-bold text-xl">{student.name?.charAt(0)}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-white font-semibold text-lg truncate">{student.name}</h3>
                                {student.is_sifu && (
                                  <span className="px-2 py-0.5 text-xs rounded-sm bg-[#D4AF37] text-black font-bold whitespace-nowrap">師父 SIFU</span>
                                )}
                              </div>
                              
                              {/* Pillar & Level Display */}
                              <div className="mt-2 space-y-2">
                                {student.adult_pillar ? (
                                  <div>
                                    <span className={`inline-flex px-2 py-1 text-xs rounded-sm text-white ${PILLAR_COLORS[student.adult_pillar] || "bg-gray-500"}`}>
                                      {student.adult_pillar.split(" – ")[0]}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-[#A1A1AA] text-xs">No pillar assigned</span>
                                )}
                                
                                {student.adult_level ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-white text-sm font-medium">{student.adult_level}</span>
                                    <span className="text-[#A1A1AA] text-xs">({pillarProgress}% of pillar)</span>
                                  </div>
                                ) : (
                                  <span className="text-[#A1A1AA] text-xs">No level assigned</span>
                                )}
                                
                                {/* Pillar Progress Bar */}
                                {student.adult_pillar && (
                                  <div className="mt-1">
                                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                      <div 
                                        className={`h-full ${PILLAR_COLORS[student.adult_pillar] || "bg-gray-500"} transition-all`}
                                        style={{ width: `${pillarProgress}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Current Form Focus */}
                          <div className="xl:w-64 flex-shrink-0">
                            <p className="text-[#A1A1AA] text-xs uppercase mb-2">Current Form Focus</p>
                            {student.current_form ? (
                              <div className="bg-[#18181b] p-3 rounded-sm">
                                <p className="text-[#D4AF37] font-medium">{student.current_form.english_name}</p>
                                <p className="text-[#A1A1AA] text-sm">{student.current_form.cantonese}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className={`px-2 py-0.5 text-xs rounded-sm text-white ${PROFICIENCY_COLORS[student.current_proficiency] || "bg-gray-500"}`}>
                                    {student.current_proficiency}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="bg-[#18181b] p-3 rounded-sm">
                                <p className="text-[#A1A1AA] text-sm">No form assigned</p>
                              </div>
                            )}
                          </div>

                          {/* Forms Progress Summary */}
                          <div className="flex-1 min-w-0">
                            <p className="text-[#A1A1AA] text-xs uppercase mb-2">Forms Progress</p>
                            <div className="bg-[#18181b] p-3 rounded-sm">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-white font-medium">
                                  {student.completed_forms}/{student.total_assigned_forms} Completed
                                </span>
                                <span className="text-[#D4AF37] font-bold">{student.progress_percentage}%</span>
                              </div>
                              <Progress value={student.progress_percentage} className="h-2 bg-white/10" />
                              
                              {/* Forms by Category Summary */}
                              {student.total_assigned_forms > 0 && (
                                <div className="flex items-center gap-4 mt-3 text-xs">
                                  <div className="flex items-center gap-1">
                                    <Hand className="w-3 h-3 text-[#A1A1AA]" />
                                    <span className="text-[#A1A1AA]">Hand: {handForms.length}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Swords className="w-3 h-3 text-[#A1A1AA]" />
                                    <span className="text-[#A1A1AA]">Weapons: {weapons.length}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Users2 className="w-3 h-3 text-[#A1A1AA]" />
                                    <span className="text-[#A1A1AA]">Sparring: {sparring.length}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Forms In Progress - Expandable Section */}
                        {student.forms_in_progress?.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <p className="text-[#A1A1AA] text-xs uppercase mb-3">Forms In Progress ({student.forms_in_progress.length})</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {student.forms_in_progress.map((form, idx) => (
                                <div key={idx} className="flex items-center justify-between gap-2 bg-[#18181b] px-3 py-2 rounded-sm">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-lg">
                                      {form.category === "Hand Form" ? "✋" : form.category === "Weapons" ? "⚔️" : "🥋"}
                                    </span>
                                    <span className="text-white text-sm truncate">{form.english_name}</span>
                                  </div>
                                  <span className={`px-2 py-0.5 text-xs rounded-sm text-white flex-shrink-0 ${PROFICIENCY_COLORS[form.proficiency] || "bg-gray-500"}`}>
                                    {form.proficiency}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==========================================
            KIDS TAB - Belt distribution & progress
            ========================================== */}
        <TabsContent value="kids" className="space-y-6">
          {/* Kids Belt Distribution */}
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-6">
              <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Belt Distribution
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                {Object.entries(beltDistribution).map(([belt, count]) => {
                  if (count === 0) return null;
                  return (
                    <div key={belt} className={`${BELT_COLORS[belt] || "bg-gray-500 text-white"} rounded-sm p-3 text-center`}>
                      <p className="text-xs font-medium truncate">{belt}</p>
                      <p className="text-lg font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>{count}</p>
                    </div>
                  );
                })}
                {Object.keys(beltDistribution).length === 0 && (
                  <p className="text-[#A1A1AA] text-center py-4 col-span-full">No belt data available</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Kids Students List */}
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-6">
              <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Kids Students
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {kidsStudents.length === 0 ? (
                <p className="text-[#A1A1AA] text-center py-4">No kids enrolled</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {kidsStudents.map((student) => (
                    <div key={student.id} className="bg-[#27272a] rounded-sm p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                          (student.class_types || [student.class_type]).join(' ').includes("Tigers") ? "bg-orange-500" : "bg-blue-500"
                        }`}>
                          <span className="text-white font-bold">{student.name?.charAt(0)}</span>
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{student.name}</h3>
                          <p className="text-[#A1A1AA] text-xs">{(student.class_types || [student.class_type]).join(', ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-1 text-xs rounded-sm ${BELT_COLORS[student.belt_rank] || "bg-gray-500 text-white"}`}>
                          {student.belt_rank || 'N/A'}
                        </span>
                        {student.current_proficiency && (
                          <span className={`px-2 py-0.5 text-xs rounded-sm text-white ${PROFICIENCY_COLORS[student.current_proficiency] || "bg-gray-500"}`}>
                            {student.current_proficiency}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
